import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import type { Database } from "../db/types.ts";
import { getBrowserClient } from "../supabase/browser.ts";

export type ReportRow = Database["public"]["Tables"]["reports"]["Row"];

export interface UseReportsState {
  reports: ReportRow[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useReports(): UseReportsState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { user } = useAuthSession();
  const [reports, setReports] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    if (!user) {
      setReports([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await supabase
      .from("reports")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(50);
    if (fetchError) {
      setReports([]);
      setError(fetchError.message);
    } else {
      setReports(data ?? []);
    }
    setLoading(false);
  }, [supabase, user]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return useMemo<UseReportsState>(
    () => ({ reports, loading, error, refetch }),
    [reports, loading, error, refetch],
  );
}
